/**
 * Voice-capture custody (account-change recording plan rev 4.1, sections 3,
 * 5 and 6). One record per accepted capture, created at admission while the
 * owner runtime's lease is held, and kept in module memory so it outlives the
 * voice provider, an editor, or an account change.
 *
 * Each record owns its capture's identity and bytes. Exactly one claimant
 * (a normal Stop, an interruption, a provider teardown, or an explicit
 * Cancel) moves it out of `recording`; the recorder's own stop event is routed
 * here once and advances only the record that expects it. Bytes end `durable`
 * (through `db.commitCaptureRow`), `failed` (kept in memory, retried as
 * decided), or `unresolved` (stop event overdue, kept). Only an explicit
 * Cancel discards. Retirement forbids further effects; it does not finish
 * outstanding work.
 *
 * Mic admission, the sign-out/update fence and the owner-replacement lease
 * read these records (sections 5.1 to 5.3). The owner-transition drain retries
 * and then refuses replacement while anything is unsaved (section 6.4).
 */
import type { AudioRecoveryDestination } from '@/types/audio-recovery';
import type { ScriptureAnchor } from '@/types/prayer';
import * as db from '@/lib/db';
import { commitCustodyCapture, setCaptureContinuationGuard } from '@/lib/audio-queue';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { parseScriptureAnchor } from '@/lib/scripture-anchor';
import {
  registerOwnerRuntimeWorkSource,
  registerOwnerTransitionDrain,
  type OwnerRuntimeWorkState,
} from '@/lib/owner-transition';
import { setVoiceWorkSource } from '@/lib/voice-work-fence';

/** Marks a stop event overdue; it never discards (decision, section 13). */
export const RECORDER_SETTLE_MS = 5_000;
let recorderSettleMs = RECORDER_SETTLE_MS;

export type CustodyDurability =
  | 'starting'
  | 'recording'
  | 'finalizing'
  | 'cancelling'
  | 'unresolved'
  | 'payload'
  | 'committing'
  | 'failed'
  | 'durable'
  | 'empty'
  | 'cancelled';
export type CustodyPipeline = 'none' | 'active' | 'finished';
export type CustodyFailureClass =
  | 'auth-unsettled'
  | 'transient-storage'
  | 'write-barrier'
  | 'identity-conflict'
  | 'owner-binding-refused';
export type CustodyStopClaimant = 'stop' | 'interruption' | 'teardown' | 'cancel';
export type CustodyClaimResult = 'claimed' | 'joined' | 'starting' | 'none';

export interface CustodyAdmission {
  custodyId: string;
  /** The voice provider instance that admitted the capture. */
  runtimeId: string;
  ownerId: string;
  authEpoch: number;
  mode: 'enhanced' | 'browser';
  destinationId: string;
  sourceDeviceId: string;
  language: string;
  recoveryDestination?: AudioRecoveryDestination;
  inlineSaveSource: 'prayer' | 'bookmark';
  scriptureAnchor?: ScriptureAnchor;
  captureToken?: string;
}

interface CustodyRecord extends CustodyAdmission {
  capturedAt: string;
  durability: CustodyDurability;
  pipeline: CustodyPipeline;
  retired: boolean;
  claimant: CustodyStopClaimant | null;
  /** No claimant code is waiting (a teardown, or a recorder that stopped by
   * itself): custody commits the payload on its own. */
  selfCommit: boolean;
  stopRequestedWhileStarting: boolean;
  awaitingInlineSave: boolean | undefined;
  payload: Blob | string | null;
  mimeType: string;
  evidence: db.CaptureEvidenceV1 | null;
  failure: { failureClass: CustodyFailureClass; error: Error } | null;
  retriedTriggers: Set<string>;
  commitInFlight: Promise<CustodyCommitOutcome> | null;
  overdueTimer: ReturnType<typeof setTimeout> | null;
  row: db.AudioQueueRow | null;
}

export type CustodyPayloadOutcome =
  | { kind: 'payload'; payload: Blob | string; mimeType: string }
  | { kind: 'empty' }
  | { kind: 'cancelled' }
  /** The record is gone and did not end empty or cancelled. Its end is read
   * with `awaitCaptureStopResult`, never inferred from the absence. */
  | { kind: 'ended' };

export type CustodyCommitOutcome =
  | { kind: 'durable'; created: boolean; row: db.AudioQueueRow }
  | { kind: 'failed'; failureClass: CustodyFailureClass; error: Error }
  | { kind: 'not-ready' };

/** How a capture ended, for a claimant that joined another's end. */
export type CustodyEndOutcome =
  | { kind: 'durable' }
  | { kind: 'failed'; error: Error }
  | { kind: 'empty' }
  | { kind: 'cancelled' };

export interface CustodyCommitOptions {
  deferProcessing: boolean;
  /** A normal Stop continues with upload and transcription after durability. */
  continuePipeline: boolean;
  publishToList: boolean;
  /** When given, the capture must belong to this owner. */
  expectedOwnerId?: string;
  /** The first claimant decides the inline-save posture; a retry keeps it. */
  awaitingInlineSave?: boolean;
  inlineSaveSource?: 'prayer' | 'bookmark';
}

/** What a Stop caller receives: the same shape as the voice hook's result. */
export type CaptureStopResult =
  | { durability: 'committed' }
  | { durability: 'not-required' }
  | { durability: 'failed'; error: Error };

const records = new Map<string, CustodyRecord>();
const listeners = new Set<() => void>();

/**
 * How removed records ended, so a caller that joined another's end (or asks
 * after removal) reads the real outcome instead of inferring empty from the
 * record's absence. Only the outcome is kept, never a payload; the oldest
 * entries are dropped past the bound. A failed record is never removed.
 */
const ENDED_OUTCOME_LIMIT = 256;
const endedOutcomes = new Map<string, CustodyEndOutcome>();

function notify(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // Observers only.
    }
  }
}

export function subscribeCustody(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function clearOverdue(record: CustodyRecord): void {
  if (record.overdueTimer !== null) clearTimeout(record.overdueTimer);
  record.overdueTimer = null;
}

function rememberEndedOutcome(record: CustodyRecord): void {
  let outcome: CustodyEndOutcome;
  if (record.durability === 'durable') outcome = { kind: 'durable' };
  else if (record.durability === 'empty') outcome = { kind: 'empty' };
  else if (record.durability === 'cancelled') outcome = { kind: 'cancelled' };
  else return;
  endedOutcomes.delete(record.custodyId);
  endedOutcomes.set(record.custodyId, outcome);
  while (endedOutcomes.size > ENDED_OUTCOME_LIMIT) {
    const oldest = endedOutcomes.keys().next().value;
    if (oldest === undefined) break;
    endedOutcomes.delete(oldest);
  }
}

/** A record is removed only by its own completion (sections 5.1, 5.2). */
function maybeRemove(record: CustodyRecord): void {
  const settled =
    record.durability === 'durable' ||
    record.durability === 'empty' ||
    record.durability === 'cancelled';
  if (!settled || record.pipeline === 'active') return;
  clearOverdue(record);
  if (records.get(record.custodyId) !== record) return;
  records.delete(record.custodyId);
  rememberEndedOutcome(record);
}

function armOverdue(record: CustodyRecord): void {
  clearOverdue(record);
  record.overdueTimer = setTimeout(() => {
    record.overdueTimer = null;
    if (record.durability === 'finalizing') {
      record.durability = 'unresolved';
      notify();
    }
  }, recorderSettleMs);
}

function defaultAwaitingInlineSave(
  record: CustodyRecord,
  claimant: CustodyStopClaimant,
): boolean | undefined {
  // A normal Stop parks new-prayer and bookmark audio for the explicit inline
  // save; an interruption or teardown parks only bookmark audio (the resume
  // card owns new-prayer recovery). Both are today's semantics.
  if (claimant === 'stop') return record.recoveryDestination ? false : true;
  return !record.recoveryDestination && record.inlineSaveSource === 'bookmark'
    ? true
    : undefined;
}

// ---- Admission and recorder lifecycle ---------------------------------------

export function admitCapture(admission: CustodyAdmission): void {
  if (records.has(admission.custodyId)) throw new Error('voice-custody-duplicate');
  records.set(admission.custodyId, {
    ...admission,
    capturedAt: new Date().toISOString(),
    durability: admission.mode === 'enhanced' ? 'starting' : 'recording',
    pipeline: 'none',
    retired: false,
    claimant: null,
    selfCommit: false,
    stopRequestedWhileStarting: false,
    awaitingInlineSave: undefined,
    payload: null,
    mimeType: admission.mode === 'enhanced' ? '' : 'text/plain',
    evidence: null,
    failure: null,
    retriedTriggers: new Set(),
    commitInFlight: null,
    overdueTimer: null,
    row: null,
  });
  notify();
}

/** A start refused before anything was recorded leaves no record. */
export function revokeCaptureAdmission(custodyId: string): void {
  const record = records.get(custodyId);
  if (!record || (record.durability !== 'starting' && record.durability !== 'recording')) return;
  clearOverdue(record);
  records.delete(custodyId);
  notify();
}

/** False when the capture was retired or stopped while the microphone
 * started, or its owner or auth epoch is no longer current. */
export function captureMayStartRecording(custodyId: string): boolean {
  const record = records.get(custodyId);
  return Boolean(
    record &&
    record.durability === 'starting' &&
    !record.retired &&
    !record.stopRequestedWhileStarting &&
    getAuthUserId() === record.ownerId &&
    getAuthEpoch() === record.authEpoch,
  );
}

export function markCaptureRecording(custodyId: string, mode: 'enhanced' | 'browser'): void {
  const record = records.get(custodyId);
  if (!record || record.durability !== 'starting') return;
  record.mode = mode;
  record.mimeType = mode === 'browser' ? 'text/plain' : record.mimeType;
  record.durability = 'recording';
  notify();
}

/** Nothing was captured: a failed start, a retired start, or an ended session. */
export function endCaptureWithoutPayload(custodyId: string): void {
  const record = records.get(custodyId);
  if (!record) return;
  if (record.durability === 'cancelling') {
    record.durability = 'cancelled';
  } else if (
    record.durability === 'starting' ||
    record.durability === 'recording' ||
    record.durability === 'finalizing' ||
    record.durability === 'unresolved'
  ) {
    record.durability = 'empty';
  } else {
    return;
  }
  clearOverdue(record);
  maybeRemove(record);
  notify();
}

/**
 * The single claim (section 3.5). `claimed`: this caller moved the capture out
 * of `recording` and must stop the recorder; nobody rebinds its handlers.
 * `joined`: another claimant owns the end; wait for it. `starting`: no
 * recorder yet; the start ends empty. A teardown also retires the capture.
 */
export function claimCaptureStop(
  custodyId: string,
  claimant: CustodyStopClaimant,
): CustodyClaimResult {
  const record = records.get(custodyId);
  if (!record) return 'none';
  if (claimant === 'teardown') record.retired = true;
  if (record.durability === 'starting') {
    if (claimant !== 'teardown') record.stopRequestedWhileStarting = true;
    notify();
    return 'starting';
  }
  if (record.durability !== 'recording') {
    notify();
    return 'joined';
  }
  record.claimant = claimant;
  record.selfCommit = claimant === 'teardown';
  record.durability = claimant === 'cancel' ? 'cancelling' : 'finalizing';
  record.awaitingInlineSave = claimant === 'cancel'
    ? undefined
    : defaultAwaitingInlineSave(record, claimant);
  armOverdue(record);
  notify();
  return 'claimed';
}

/**
 * The recorder's stop event, routed here exactly once per recorder. Advances
 * only a record in a state that expects it (compare-and-set): a late or
 * duplicate event changes nothing. A recorder that stopped on its own while
 * recording keeps its bytes as an interruption would.
 */
export function deliverRecorderStop(
  custodyId: string,
  blob: Blob | null,
  mimeType: string,
): boolean {
  const record = records.get(custodyId);
  if (!record) return false;
  if (record.durability === 'cancelling') {
    record.durability = 'cancelled';
    clearOverdue(record);
    maybeRemove(record);
    notify();
    return true;
  }
  if (record.durability === 'recording') {
    record.claimant = 'interruption';
    record.selfCommit = true;
    record.awaitingInlineSave = defaultAwaitingInlineSave(record, 'interruption');
    record.durability = 'finalizing';
  }
  if (record.durability !== 'finalizing' && record.durability !== 'unresolved') return false;
  clearOverdue(record);
  if (blob && blob.size > 0) {
    record.payload = blob;
    record.mimeType = mimeType || blob.type || 'audio/webm';
    record.durability = 'payload';
  } else {
    record.durability = 'empty';
  }
  notify();
  if (record.durability === 'empty') {
    maybeRemove(record);
    notify();
    return true;
  }
  if (record.selfCommit) commitOnItsOwn(record);
  return true;
}

function commitOnItsOwn(record: CustodyRecord): void {
  void commitCapture(record.custodyId, record.claimant === 'teardown' ? 'teardown' : 'interruption', {
    deferProcessing: record.retired,
    continuePipeline: false,
    publishToList: !record.retired,
  });
}

/** Browser speech: the words this page holds become the capture's payload. */
export function deliverCaptureText(custodyId: string, text: string): boolean {
  const record = records.get(custodyId);
  if (!record || record.mode !== 'browser') return false;
  if (record.durability !== 'finalizing' && record.durability !== 'unresolved') return false;
  clearOverdue(record);
  const words = text.trim();
  if (words) {
    record.payload = words;
    record.mimeType = 'text/plain';
    record.durability = 'payload';
  } else {
    record.durability = 'empty';
  }
  notify();
  if (record.durability === 'empty') {
    maybeRemove(record);
    notify();
    return true;
  }
  if (record.selfCommit) commitOnItsOwn(record);
  return true;
}

function waitFor<T>(evaluate: () => T | undefined): Promise<T> {
  return new Promise((resolve) => {
    const check = (): boolean => {
      const value = evaluate();
      if (value === undefined) return false;
      unsubscribe();
      resolve(value);
      return true;
    };
    const unsubscribe = subscribeCustody(() => { check(); });
    check();
  });
}

/** Resolves once the stop event (or its absence) has decided the payload. */
export function awaitCapturePayload(custodyId: string): Promise<CustodyPayloadOutcome> {
  return waitFor<CustodyPayloadOutcome>(() => {
    const record = records.get(custodyId);
    if (!record) {
      const ended = endedOutcomes.get(custodyId);
      if (ended?.kind === 'empty' || ended?.kind === 'cancelled') return ended;
      return { kind: 'ended' };
    }
    if (record.payload !== null) {
      return { kind: 'payload', payload: record.payload, mimeType: record.mimeType };
    }
    if (record.durability === 'empty') return { kind: 'empty' };
    if (record.durability === 'cancelled') return { kind: 'cancelled' };
    return undefined;
  });
}

/** For a claimant that joined: resolves at durable, failed, empty or cancelled.
 * A removed record answers with its kept outcome; an outcome that is not
 * known (never tracked, or past the bound) is a failure, never empty. */
export function awaitCaptureEnd(custodyId: string): Promise<CustodyEndOutcome> {
  return waitFor<CustodyEndOutcome>(() => {
    const record = records.get(custodyId);
    if (!record) {
      return endedOutcomes.get(custodyId) ??
        { kind: 'failed', error: new Error('voice-custody-outcome-unknown') };
    }
    if (record.durability === 'durable') return { kind: 'durable' };
    if (record.durability === 'failed' && record.failure) {
      return { kind: 'failed', error: record.failure.error };
    }
    if (record.durability === 'empty') return { kind: 'empty' };
    if (record.durability === 'cancelled') return { kind: 'cancelled' };
    return undefined;
  });
}

/** The joined (or late) Stop caller's result: committed only for a durable
 * end, failed for a failure or an unknown end, not-required for empty or
 * Cancel. */
export function awaitCaptureStopResult(custodyId: string): Promise<CaptureStopResult> {
  return awaitCaptureEnd(custodyId).then((end): CaptureStopResult => {
    if (end.kind === 'durable') return { durability: 'committed' };
    if (end.kind === 'failed') return { durability: 'failed', error: end.error };
    return { durability: 'not-required' };
  });
}

// ---- Persistence ---------------------------------------------------------------

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

async function evidenceFor(record: CustodyRecord): Promise<db.CaptureEvidenceV1> {
  if (record.evidence) return record.evidence;
  const payload = record.payload;
  if (payload instanceof Blob) {
    record.evidence = {
      schema: 'adia-capture-evidence-v1',
      kind: 'blob',
      sha256: await sha256Hex(await payload.arrayBuffer()),
      sizeBytes: payload.size,
      mimeType: record.mimeType,
    };
  } else if (typeof payload === 'string') {
    const encoded = new TextEncoder().encode(payload);
    record.evidence = {
      schema: 'adia-capture-evidence-v1',
      kind: 'text',
      sha256: await sha256Hex(
        encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength),
      ),
      sizeBytes: 0,
      mimeType: 'text/plain',
    };
  } else {
    throw new db.CaptureCommitError('capture-input-invalid', 'payload');
  }
  return record.evidence;
}

async function captureRow(record: CustodyRecord): Promise<db.AudioQueueRow> {
  const evidence = await evidenceFor(record);
  const prayerId = record.recoveryDestination?.prayerId ?? null;
  const scriptureAnchor = record.scriptureAnchor
    ? parseScriptureAnchor(record.scriptureAnchor) ?? undefined
    : undefined;
  const row: db.AudioQueueRow = {
    id: record.custodyId,
    ownerId: record.ownerId,
    authEpochAtCapture: record.authEpoch,
    audioProtocolVersion: 1,
    captureMode: record.mode,
    destinationId: record.destinationId,
    sourceDeviceId: record.sourceDeviceId,
    prayerId,
    capturedAt: record.capturedAt,
    language: record.language,
    attemptCount: 0,
    failed: false,
    recoveryKind:
      record.recoveryDestination?.kind ??
      (record.inlineSaveSource === 'bookmark' ? 'bookmark' : 'new-prayer'),
    inlineSaveSource: record.inlineSaveSource,
    ...(record.awaitingInlineSave !== undefined
      ? { awaitingInlineSave: record.awaitingInlineSave }
      : {}),
    ...(scriptureAnchor ? { scriptureAnchor } : {}),
    sizeBytes: evidence.sizeBytes,
    mimeType: evidence.mimeType,
    transcribedText: null,
    captureEvidence: evidence,
    captureIdentity: {
      schema: 'adia-capture-identity-v1',
      custodyId: record.custodyId,
      capturedOwnerId: record.ownerId,
      capturedAuthEpoch: record.authEpoch,
      capturedPrayerId: prayerId,
      sourceDeviceId: record.sourceDeviceId,
    },
  };
  if (record.payload instanceof Blob) {
    row.blob = record.payload;
  } else {
    row.transcribedText = record.payload as string;
  }
  return row;
}

function classifyFailure(error: unknown): CustodyFailureClass {
  if (error instanceof db.CaptureCommitError) {
    if (error.code === 'auth-unsettled') return 'auth-unsettled';
    if (error.code === 'owner-binding-refused') return 'owner-binding-refused';
    // A conflicting row and an input the guard refuses are never retried.
    return 'identity-conflict';
  }
  const message = error instanceof Error ? error.message : '';
  if (message.startsWith('owner transition active:')) return 'write-barrier';
  return 'transient-storage';
}

/**
 * Commit the capture's payload to its own id (section 4.2): `created`, or
 * `verified` when the identical capture is already stored. A failure keeps
 * the payload in memory with its class. Overlapping callers join one commit.
 */
export function commitCapture(
  custodyId: string,
  claimant: db.CaptureClaimant,
  options: CustodyCommitOptions,
): Promise<CustodyCommitOutcome> {
  const record = records.get(custodyId);
  if (!record) return Promise.resolve({ kind: 'not-ready' });
  if (record.commitInFlight) return record.commitInFlight;
  if (record.durability === 'durable' && record.row) {
    return Promise.resolve({ kind: 'durable', created: false, row: record.row });
  }
  if (record.durability !== 'payload' && record.durability !== 'failed') {
    return Promise.resolve({ kind: 'not-ready' });
  }
  if (options.expectedOwnerId !== undefined && options.expectedOwnerId !== record.ownerId) {
    return Promise.resolve({
      kind: 'failed',
      failureClass: 'owner-binding-refused',
      error: new Error('voice-custody-owner-mismatch'),
    });
  }
  if (options.awaitingInlineSave !== undefined && record.row === null && record.evidence === null) {
    record.awaitingInlineSave = options.awaitingInlineSave;
  }
  if (options.inlineSaveSource !== undefined && options.inlineSaveSource !== record.inlineSaveSource) {
    return Promise.resolve({
      kind: 'failed',
      failureClass: 'identity-conflict',
      error: new Error('voice-custody-source-mismatch'),
    });
  }
  record.durability = 'committing';
  notify();
  const pending = (async (): Promise<CustodyCommitOutcome> => {
    try {
      const row = await captureRow(record);
      const result = await commitCustodyCapture({
        row,
        claimant,
        deferProcessing: options.deferProcessing || record.retired,
        publishToList: options.publishToList && !record.retired,
      });
      record.durability = 'durable';
      record.failure = null;
      record.row = result.row;
      if (options.continuePipeline && !record.retired) record.pipeline = 'active';
      return { kind: 'durable', created: result.status === 'created', row: result.row };
    } catch (error) {
      const failure = error instanceof Error ? error : new Error('voice-custody-commit-failed');
      const failureClass = classifyFailure(error);
      record.durability = 'failed';
      record.failure = { failureClass, error: failure };
      return { kind: 'failed', failureClass, error: failure };
    } finally {
      record.commitInFlight = null;
      maybeRemove(record);
      notify();
    }
  })();
  record.commitInFlight = pending;
  return pending;
}

/**
 * A claimant that could not even attempt the commit (its lock or identity
 * failed) keeps the payload as a failed capture rather than leaving it waiting.
 */
export function markCaptureFailed(custodyId: string, error: Error): void {
  const record = records.get(custodyId);
  if (!record || record.durability !== 'payload' || record.commitInFlight) return;
  record.durability = 'failed';
  record.failure = { failureClass: 'transient-storage', error };
  notify();
}

// ---- Pipeline and retirement -----------------------------------------------------

export function isCaptureRetired(custodyId: string): boolean {
  return records.get(custodyId)?.retired ?? true;
}

/**
 * May this capture's pipeline take its next step (a network call, a row
 * write, a list update, editor delivery)? Not retired, and its original
 * owner and auth epoch still current, so a held response cannot act for it
 * even after A to B to A. Registered with the audio queue, which asks before
 * every step of upload, registration and transcription. An id custody does
 * not track is not a live capture's pipeline (the background processor's
 * rows), so it is not refused here.
 */
export function captureMayContinue(custodyId: string): boolean {
  const record = records.get(custodyId);
  if (!record) return true;
  return !record.retired &&
    getAuthUserId() === record.ownerId &&
    getAuthEpoch() === record.authEpoch;
}

/** Retirement forbids further effects; an in-flight step returns, then finishes. */
export function retireCapture(custodyId: string): void {
  const record = records.get(custodyId);
  if (!record || record.retired) return;
  record.retired = true;
  notify();
}

export function retireRuntimeCaptures(runtimeId: string): void {
  let changed = false;
  for (const record of records.values()) {
    if (record.runtimeId === runtimeId && !record.retired) {
      record.retired = true;
      changed = true;
    }
  }
  if (changed) notify();
}

export function finishCapturePipeline(custodyId: string): void {
  const record = records.get(custodyId);
  if (!record || record.pipeline !== 'active') return;
  record.pipeline = 'finished';
  maybeRemove(record);
  notify();
}

/** Browser captures of a torn-down provider still waiting for their words. */
export function browserCapturesAwaitingText(runtimeId: string): string[] {
  return [...records.values()]
    .filter((record) =>
      record.runtimeId === runtimeId &&
      record.mode === 'browser' &&
      record.claimant === 'teardown' &&
      record.durability === 'finalizing')
    .map((record) => record.custodyId);
}

// ---- Retries (section 3.6, decision R3) ------------------------------------------

const AUTOMATIC_RETRY_CLASSES = new Set<CustodyFailureClass>(['transient-storage', 'write-barrier']);

/**
 * One automatic retry per record per trigger key (`marker:<id>` or
 * `mount:<provider id>`). A retry already in flight is joined and the key is
 * recorded as used. A retry's own outcome never produces a trigger.
 */
export function retryFailedCaptures(trigger: string, ownerId?: string): Promise<unknown> {
  const pending: Promise<unknown>[] = [];
  for (const record of [...records.values()]) {
    if (ownerId !== undefined && record.ownerId !== ownerId) continue;
    if (record.retriedTriggers.has(trigger)) continue;
    if (record.commitInFlight) {
      record.retriedTriggers.add(trigger);
      pending.push(record.commitInFlight);
      continue;
    }
    if (record.durability !== 'failed' || !record.failure) continue;
    if (!AUTOMATIC_RETRY_CLASSES.has(record.failure.failureClass)) continue;
    record.retriedTriggers.add(trigger);
    pending.push(commitCapture(record.custodyId, 'retry', {
      deferProcessing: record.retired,
      continuePipeline: false,
      publishToList: !record.retired,
    }));
  }
  return Promise.all(pending);
}

/** Explicit Try again: every failed record, whatever its class. */
export async function tryAgainUnsavedCaptures(): Promise<boolean> {
  const failed = [...records.values()].filter((record) => record.durability === 'failed');
  const outcomes = await Promise.all(failed.map((record) => commitCapture(record.custodyId, 'retry', {
    deferProcessing: record.retired,
    continuePipeline: false,
    publishToList: !record.retired,
  })));
  return outcomes.every((outcome) => outcome.kind === 'durable');
}

// ---- Reads for admission, fence, lease, drain and warning ----------------------

export function hasCustodyRecords(): boolean {
  return records.size > 0;
}

/** Section 5.1: every state of a present record refuses a new capture. */
export function isCustodyAdmissionBusy(): boolean {
  return records.size > 0;
}

const WAITING = new Set<CustodyDurability>(['starting', 'recording', 'finalizing', 'payload', 'committing']);
const BLOCKING = new Set<CustodyDurability>(['unresolved', 'failed']);

/**
 * Section 5.3: does this record hold account replacement while it settles?
 * A retired capture still starting has nothing to save (its late stream is
 * stopped and creates no recorder), so it ends its hold at retirement; mic
 * admission and the fence stay held until its pending start returns.
 */
function holdsReplacement(record: CustodyRecord): boolean {
  return WAITING.has(record.durability) && !(record.durability === 'starting' && record.retired);
}

/** Section 5.3, for the replaced owner's records only. */
export function ownerReplacementState(ownerId: string): OwnerRuntimeWorkState {
  let blocked = false;
  for (const record of records.values()) {
    if (record.ownerId !== ownerId) continue;
    if (holdsReplacement(record)) return 'waiting';
    if (BLOCKING.has(record.durability)) blocked = true;
  }
  return blocked ? 'blocked' : 'free';
}

export interface UnsavedRecordingState {
  failed: number;
  unresolved: number;
}

/** What the unsaved-recording warning shows (section 8). */
export function unsavedRecordingState(): UnsavedRecordingState {
  let failed = 0;
  let unresolved = 0;
  for (const record of records.values()) {
    if (record.durability === 'failed') failed += 1;
    if (record.durability === 'unresolved') unresolved += 1;
  }
  return { failed, unresolved };
}

/** Section 6.4: retry for this marker, wait while anything waits, then refuse
 * replacement while anything is unresolved or failed. */
async function drainCustodyForMarker(marker: { id: string }): Promise<void> {
  await retryFailedCaptures(`marker:${marker.id}`);
  await waitFor(() => (
    [...records.values()].some(holdsReplacement) ? undefined : true
  ));
  if ([...records.values()].some((record) => BLOCKING.has(record.durability))) {
    throw new Error('voice-capture-unsaved');
  }
}

/** Probe-only: shorten the overdue mark (never in a browser). */
export function configureCustodyForProbe(options: { settleMs: number }): void {
  if (!db.isNodeProbeRuntime()) throw new Error('voice-custody-probe-config-unavailable');
  recorderSettleMs = options.settleMs;
}

/** Probe-only: the kept end outcome of a removed record (outcome only). */
export function inspectEndedOutcomeForProbe(custodyId: string): CustodyEndOutcome['kind'] | null {
  return endedOutcomes.get(custodyId)?.kind ?? null;
}

/** Probe-only snapshot of one record's axes. */
export function inspectCaptureForProbe(custodyId: string): {
  durability: CustodyDurability;
  pipeline: CustodyPipeline;
  retired: boolean;
  failureClass: CustodyFailureClass | null;
  retriedTriggers: string[];
} | null {
  const record = records.get(custodyId);
  if (!record) return null;
  return {
    durability: record.durability,
    pipeline: record.pipeline,
    retired: record.retired,
    failureClass: record.failure?.failureClass ?? null,
    retriedTriggers: [...record.retriedTriggers],
  };
}

setVoiceWorkSource(hasCustodyRecords);
setCaptureContinuationGuard(captureMayContinue);
registerOwnerRuntimeWorkSource({
  state: ownerReplacementState,
  subscribe: subscribeCustody,
  retryForMarker: (markerId) => retryFailedCaptures(`marker:${markerId}`),
});
registerOwnerTransitionDrain(drainCustodyForMarker);
